export type GamePhase = "ready" | "allocating" | "finished";
export type GameResult = "win" | "lose" | "draw" | null;

export type SlimeDicerState = {
  phase: GamePhase;
  result: GameResult;
  turn: number;
  playerHp: number;
  enemyHp: number;
  enemyAttack: number;
  enemyDefense: number;
  dice: number[];
  remaining: number;
  storedPoints: number;
  skillUsed: boolean;
  attack: number;
  defense: number;
  message: string;
  lastHit: { player: number; enemy: number } | null;
};

const randomInt = (max: number, random: () => number) => Math.floor(random() * max);

export function newSlimeDicerGame(random = Math.random): SlimeDicerState {
  return {
    phase: "ready", result: null, turn: 1, playerHp: 50, enemyHp: 50,
    enemyAttack: randomInt(10, random), enemyDefense: randomInt(10, random),
    dice: [], remaining: 0, storedPoints: 0, skillUsed: false, attack: 0, defense: 0,
    message: "주사위 세 개를 굴려 전투를 시작하세요.", lastHit: null,
  };
}

export function rollSlimeDicerDice(state: SlimeDicerState, random = Math.random): SlimeDicerState {
  if (state.phase !== "ready") return state;
  const dice = Array.from({ length: 3 }, () => randomInt(6, random) + 1);
  return { ...state, phase: "allocating", dice, remaining: dice.reduce((a, b) => a + b, 0),
    message: "주사위 점수를 공격과 방어에 배분하세요.", lastHit: null };
}

export function allocateSlimeDicerPoint(state: SlimeDicerState, stat: "attack" | "defense", change: -1 | 1): SlimeDicerState {
  if (state.phase !== "allocating" || (change > 0 && state.remaining === 0) || (change < 0 && state[stat] === 0)) return state;
  return { ...state, [stat]: state[stat] + change, remaining: state.remaining - change };
}

export function activateSlimeDicerSkill(state: SlimeDicerState): SlimeDicerState {
  if (state.phase !== "allocating" || state.skillUsed || state.storedPoints < 15) return state;
  return { ...state, storedPoints: state.storedPoints - 15, skillUsed: true,
    message: "스킬 준비 완료! 이번 턴 공격력이 15 증가합니다." };
}

export function resolveSlimeDicerTurn(state: SlimeDicerState, random = Math.random): SlimeDicerState {
  if (state.phase !== "allocating") return state;
  const playerHit = Math.max(0, state.enemyAttack - state.defense);
  const enemyHit = Math.max(0, state.attack + (state.skillUsed ? 15 : 0) - state.enemyDefense);
  const playerHp = Math.max(0, state.playerHp - playerHit);
  const enemyHp = Math.max(0, state.enemyHp - enemyHit);
  const result: GameResult = playerHp === 0 && enemyHp === 0 ? "draw" : enemyHp === 0 ? "win" : playerHp === 0 ? "lose" : null;
  const message = result === "win" ? "승리! 적 슬라임을 쓰러뜨렸습니다." : result === "lose" ? "패배! 다시 도전해 보세요." : result === "draw" ? "무승부! 두 슬라임이 동시에 쓰러졌습니다." :
    `적에게 ${enemyHit} 피해를 주고 ${playerHit} 피해를 받았습니다.${state.skillUsed ? " 스킬 공격력 +15 적용." : ""} 다음 주사위를 굴리세요.`;
  return { ...state, phase: result ? "finished" : "ready", result, turn: result ? state.turn : state.turn + 1,
    playerHp, enemyHp, enemyAttack: result ? state.enemyAttack : randomInt(18, random),
    enemyDefense: result ? state.enemyDefense : randomInt(15, random),
    dice: [], remaining: 0, storedPoints: state.storedPoints + state.remaining,
    skillUsed: false, attack: 0, defense: 0, message,
    lastHit: { player: playerHit, enemy: enemyHit } };
}
